import type { ISubscriptionInfo } from "@/account/account-models";
import { createReducer } from "@reduxjs/toolkit";
import type {
  IGetBroadcastStatsConfig,
  IGetPayoutsConfig,
  IGetReferrersConfig,
  IGetSubscribersConfig,
  IGetUserStatsConfig,
} from "./earnings-action-creators";
import { IEarningsActions } from "./earnings-actions";
import type {
  IBroadcastStatsItem,
  IPayout,
  IReferralsStatSummary,
  IReferralStatsItem,
  IUserStatsItem,
} from "./earnings-models";

export interface IEarningsModel {
  nextPayoutsUrl: string | null;
  payouts: IPayout[] | null;
  nextSubscribersUrl: string | null;
  subscribers: ISubscriptionInfo[] | null;
  nextReferrersUrl: string | null;
  referrers: IReferralStatsItem[] | null;
  referrersSummary: IReferralsStatSummary | null;
  nextBroadcastStatsUrl: string | null;
  broadcastStats: IBroadcastStatsItem[] | null;
  nextUserStatsUrl: string | null;
  userStats: IUserStatsItem[] | null;
}

const INITIAL_STATE: IEarningsModel = {
  nextPayoutsUrl: null,
  payouts: null,
  nextSubscribersUrl: null,
  subscribers: null,
  nextReferrersUrl: null,
  referrers: null,
  referrersSummary: null,
  nextBroadcastStatsUrl: null,
  broadcastStats: null,
  nextUserStatsUrl: null,
  userStats: null,
};

/** Reducer */
export const earningsReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(
      IEarningsActions.successGetPayouts,
      (state, action): IEarningsModel => {
        const config: IGetPayoutsConfig = action.payload.config;
        const res = action.payload.res;

        let payouts: IPayout[] = res.payouts;
        const getPayoutsNextUrl: string =
          res.payouts.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          payouts = [...(state.payouts || []), ...res.payouts];
        }

        return {
          ...state,
          payouts,
          nextPayoutsUrl: getPayoutsNextUrl,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetSubscribers,
      (state, action): IEarningsModel => {
        const config: IGetSubscribersConfig = action.payload.config;
        const res = action.payload.res;

        let subscribers: ISubscriptionInfo[] = res.data;
        const getSubscribersNextUrl: string =
          res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          subscribers = [...(state.subscribers || []), ...res.data];
        }

        return {
          ...state,
          nextSubscribersUrl: getSubscribersNextUrl,
          subscribers,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetReferrers,
      (state, action): IEarningsModel => {
        const config: IGetReferrersConfig = action.payload.config;
        const res = action.payload.res;

        let referrers: IReferralStatsItem[] = res.data;
        const getReferrersNextUrl: string = res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          referrers = [...(state.referrers || []), ...res.data];
        }

        return {
          ...state,
          nextReferrersUrl: getReferrersNextUrl,
          referrers,
          referrersSummary: res.summary,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetBroadcastStats,
      (state, action): IEarningsModel => {
        const config: IGetBroadcastStatsConfig = action.payload.config;
        const res = action.payload.res;

        let broadcastStats: IBroadcastStatsItem[] = res.data;
        const getBroadcastStatsNextUrl: string =
          res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          broadcastStats = [...(state.broadcastStats || []), ...res.data];
        }

        return {
          ...state,
          nextBroadcastStatsUrl: getBroadcastStatsNextUrl,
          broadcastStats,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetUserStats,
      (state, action): IEarningsModel => {
        const config: IGetUserStatsConfig = action.payload.config;
        const res = action.payload.res;

        let userStats: IUserStatsItem[] = res.data;
        const getUserStatsNextUrl: string = res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          userStats = [...(state.userStats || []), ...res.data];
        }

        return {
          ...state,
          nextUserStatsUrl: getUserStatsNextUrl,
          userStats,
        };
      }
    );
});
